"use client";

import NextLink, { type LinkProps as NextLinkProps } from "next/link";
import type { ComponentProps } from "react";
import { forwardRef } from "react";

export type { LinkProps } from "next/link";
export { useLinkStatus } from "next/link";

type LinkProps = ComponentProps<typeof NextLink>;

/**
 * `next/link` with viewport prefetching OFF by default.
 *
 * ## Why this wrapper exists
 *
 * Next answers a prefetch for this app's routes with a ZERO-byte body where a
 * navigation of the same URL returns 85-255 kB. Measured repeatedly against
 * production, across route families, with and without `loading.tsx`, before
 * and after `partialPrefetching`:
 *
 *     RSC: 1                                87,786 bytes
 *     RSC: 1 + Next-Router-Prefetch: 1           0 bytes
 *     RSC: 1 + Next-Router-Segment-Prefetch  1,339 bytes
 *
 * An empty prefetch is never satisfiable, so the router never records a usable
 * entry and re-requests the route on every re-evaluation of the link. Links in
 * a scrolling list are re-evaluated constantly, which turns scrolling into a
 * request amplifier: twelve programmatic `window.scrollBy` calls on a
 * production build, with no pointer events at all, produced 2,395 RSC requests
 * — 293 of them to a single URL.
 *
 * In production every one of those shares one HTTP/2 connection with the
 * navigation the user is waiting for, so the storm is not merely wasteful; it
 * is what leaves a page with no stream to load on. That is the skeleton that
 * never clears.
 *
 * ## Why the default flipped instead of being fixed per file
 *
 * It was tried per file, and it does not hold. Every `<Link>` in a list is a
 * new instance of this bug, `next/link` is imported in roughly forty places,
 * and each fix only lasted until the next list was found. A default that is
 * wrong for this app should be wrong in one place, not forty.
 *
 * ## What is NOT lost
 *
 * Nothing measurable. The prefetch these links were issuing returned no bytes,
 * so it made no navigation faster — it was pure cost. `BackAwareLink` still
 * prefetches on hover, focus and touchstart, which is the interaction that
 * actually precedes a click, and any link can opt back in with an explicit
 * `prefetch` prop.
 *
 * Revisit when the upstream prefetch returns a real payload; at that point the
 * default should go back to Next's.
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ prefetch, ...props }, ref) => (
    <NextLink ref={ref} prefetch={prefetch ?? false} {...props} />
  ),
);

Link.displayName = "Link";

export default Link;

export type { NextLinkProps };
